// src/MarketingPage.js
//
// Marketing push notifications: compose, preview, choose who, send, audit.
//
// Two rules this page is built around.
//
// First, every recipient number on screen comes from marketing_audience() —
// the same database function the send uses to pick who actually gets messaged.
// Console tools habitually show a confident audience number computed one way
// and send by another; the number then teaches you to trust it, and it is
// wrong. Here the count and the send cannot disagree, because they are one
// query.
//
// Second, nothing is sent without seeing that number and confirming it. This
// is the loudest thing the business can do to its customers and it cannot be
// recalled.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, MenuItem, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  CircularProgress, Stack, Divider, Radio, RadioGroup, FormControlLabel,
  Switch, Tooltip, LinearProgress, ToggleButton, ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from './supabaseClient';
import { brand, ink, surface, radius } from './designTokens';

import { prettyStatus } from './labels';
const TITLE_MAX = 40;   // iOS truncates the bold line past roughly this
const BODY_MAX  = 170;  // two lines on a lock screen

const STATUS_COLOR = {
  draft: 'default', scheduled: 'info', sending: 'warning',
  sent: 'success', failed: 'error', cancelled: 'default',
};

/// The three ways to pick an audience. Counts are filled in at runtime.
const AUDIENCE_OPTIONS = [
  {
    value: 'inactive_days',
    label: 'People drifting away',
    detail: 'Have not opened the app recently — the ones worth winning back',
  },
  {
    value: 'user_type',
    label: 'By account type',
    detail: 'Clients or stylists only',
  },
  {
    value: 'all',
    label: 'Everyone reachable',
    detail: 'Every user with a device that can receive push',
  },
];

export default function MarketingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'info' });
  const [confirm, setConfirm]     = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [busy, setBusy]           = useState(false);

  // Composer
  const [title, setTitle]               = useState('');
  const [body, setBody]                 = useState('');
  const [audience, setAudience]         = useState('inactive_days');
  const [inactiveDays, setInactiveDays] = useState(7);
  const [userType, setUserType]         = useState('Client');
  const [useSample, setUseSample]       = useState(false);
  const [sampleSize, setSampleSize]     = useState(50);
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [imageUrl, setImageUrl]         = useState('');
  const [uploading, setUploading]       = useState(false);

  // Campaign list controls
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView]                 = useState('table');

  // Live counts, one per audience option, from the send's own function.
  const [counts, setCounts]           = useState({});
  const [reach, setReach]             = useState(null);   // reachability health
  const [recipients, setRecipients]   = useState(null);   // {campaign, rows}
  const [countsLoading, setCountsLoading] = useState(true);

  const notify = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('marketing_campaigns')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) notify(`Could not load campaigns: ${error.message}`, 'error');
    setCampaigns(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Every option's reach, so the choice is made with the numbers visible
  // rather than after selecting blind.
  const refreshCounts = useCallback(async () => {
    setCountsLoading(true);
    const ask = async (p_audience, p_inactive_days, p_user_type) => {
      const { data, error } = await supabase.rpc('marketing_audience', {
        p_audience, p_inactive_days, p_user_type,
      });
      return error ? null : (data || []).length;
    };
    const [all, inactive, client, stylist] = await Promise.all([
      ask('all', null, null),
      ask('inactive_days', Number(inactiveDays) || 7, null),
      ask('user_type', null, 'Client'),
      ask('user_type', null, 'Stylist'),
    ]);
    setCounts({ all, inactive, client, stylist });

    // Reachability health. The gap between "users" and "users we can actually
    // reach" is the number that caps every push feature, and it is invisible
    // unless something states it plainly.
    const [{ count: profiles }, { count: optedOut }, { count: deadTokens }] = await Promise.all([
      supabase.from('Profiles').select('*', { count: 'exact', head: true }),
      supabase.from('push_preferences').select('*', { count: 'exact', head: true }).eq('marketing', false),
      supabase.from('user_devices').select('*', { count: 'exact', head: true }).eq('is_active', false),
    ]);
    setReach({ profiles: profiles ?? 0, reachable: all ?? 0, optedOut: optedOut ?? 0, deadTokens: deadTokens ?? 0 });
    setCountsLoading(false);
  }, [inactiveDays]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const selectedCount = useMemo(() => {
    if (audience === 'all') return counts.all;
    if (audience === 'inactive_days') return counts.inactive;
    return userType === 'Stylist' ? counts.stylist : counts.client;
  }, [audience, userType, counts]);

  const willReach = useMemo(() => {
    if (selectedCount == null) return null;
    if (useSample && Number(sampleSize) > 0) {
      return Math.min(Number(sampleSize), selectedCount);
    }
    return selectedCount;
  }, [selectedCount, useSample, sampleSize]);

  const canSave = title.trim().length > 0 && body.trim().length > 0;
  const overTitle = title.length > TITLE_MAX;
  const overBody  = body.length > BODY_MAX;

  const composeRow = (status) => ({
    title: title.trim(),
    body: body.trim(),
    audience,
    inactive_days: audience === 'inactive_days' ? Number(inactiveDays) || 7 : null,
    user_type: audience === 'user_type' ? userType : null,
    sample_size: useSample && Number(sampleSize) > 0 ? Number(sampleSize) : null,
    scheduled_for: scheduleLater && scheduledFor ? new Date(scheduledFor).toISOString() : null,
    image_url: imageUrl || null,
    status,
  });

  const saveCampaign = async (status) => {
    setBusy(true);
    const { data, error } = await supabase
      .from('marketing_campaigns').insert(composeRow(status)).select().single();
    setBusy(false);
    if (error) { notify(`Could not save: ${error.message}`, 'error'); return null; }
    await loadCampaigns();
    return data;
  };

  const resetComposer = () => {
    setTitle(''); setBody(''); setUseSample(false);
    setScheduleLater(false); setScheduledFor(''); setImageUrl('');
  };

  const send = async (campaign) => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('send-marketing-notification', {
      body: { campaign_id: campaign.id },
    });
    setBusy(false);
    setConfirm(null);
    if (error) {
      notify(`Send failed: ${error.message}`, 'error');
    } else {
      notify(
        `Delivered to ${data?.delivered ?? 0} of ${data?.recipient_count ?? 0}` +
        (data?.failed ? ` — ${data.failed} unreachable` : ''),
        'success',
      );
      resetComposer();
    }
    await loadCampaigns();
    refreshCounts();
  };

  const remove = async (campaign) => {
    const { error } = await supabase
      .from('marketing_campaigns').delete().eq('id', campaign.id);
    setDeleting(null);
    if (error) notify(`Could not delete: ${error.message}`, 'error');
    else { notify('Campaign deleted', 'success'); await loadCampaigns(); }
  };

  // Upload to the public bucket, deliberately.
  //
  // APNs downloads the attachment on the device with no credentials of ours, so
  // a signed or private URL arrives as a notification with no picture and no
  // error anywhere. Public is a requirement here, not laziness.
  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      notify('Images must be under 5MB — iOS will not download anything larger', 'error');
      return;
    }
    setUploading(true);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `marketing/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage
      .from('stylist-media')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (error) {
      setUploading(false);
      notify(`Upload failed: ${error.message}`, 'error');
      return;
    }
    const { data } = supabase.storage.from('stylist-media').getPublicUrl(path);
    setImageUrl(data?.publicUrl || '');
    setUploading(false);
  };

  const openRecipients = async (campaign) => {
    const { data } = await supabase
      .from('marketing_campaign_recipients')
      .select('*')
      .eq('campaign_id', campaign.id)
      .order('sent_at', { ascending: false })
      .limit(500);
    setRecipients({ campaign, rows: data || [] });
  };

  const visibleCampaigns = useMemo(() => {
    const term = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesTerm = !term
        || (c.title || '').toLowerCase().includes(term)
        || (c.body || '').toLowerCase().includes(term);
      return matchesStatus && matchesTerm;
    });
  }, [campaigns, search, statusFilter]);

  const audienceSummary = () => {
    if (audience === 'all') return 'Everyone reachable';
    if (audience === 'user_type') return `${userType}s only`;
    return `Not opened in ${inactiveDays} day${Number(inactiveDays) === 1 ? '' : 's'}`;
  };

  const headerActions = (
    <Stack direction="row" spacing={1}>
      <Button
        size="small" variant="text" startIcon={<SaveIcon />}
        disabled={!canSave || busy}
        onClick={async () => {
          if (await saveCampaign('draft')) { notify('Saved as draft', 'success'); resetComposer(); }
        }}
      >
        Save as draft
      </Button>
      <Button
        size="small" variant="outlined" startIcon={<ScheduleIcon />}
        disabled={!canSave || busy || !scheduleLater || !scheduledFor}
        onClick={async () => {
          if (await saveCampaign('scheduled')) { notify('Scheduled', 'success'); resetComposer(); }
        }}
      >
        Schedule
      </Button>
      <Button
        size="small" variant="contained" startIcon={<SendIcon />}
        disabled={!canSave || busy || willReach === 0}
        sx={{ backgroundColor: brand.gold, '&:hover': { backgroundColor: brand.goldDeep } }}
        onClick={async () => {
          const saved = await saveCampaign('draft');
          if (saved) setConfirm(saved);
        }}
      >
        Send now
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb + actions, as in the reference: the three outcomes are
          always reachable rather than buried at the bottom of a long form. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: ink[500] }}>
            Marketing / Create notification
          </Typography>
          <Typography variant="h5" sx={{ color: ink[900], fontWeight: 700 }}>
            Create notification
          </Typography>
        </Box>
        {headerActions}
      </Stack>

      {/* Reachability. Stated plainly because the gap between users and
          reachable users is what caps every push feature, and nothing else in
          the console says it. */}
      {reach && (
        <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card, mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: ink[500] }}>Users</Typography>
                <Typography variant="h6" sx={{ color: ink[900], fontWeight: 700 }}>{reach.profiles}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: ink[500] }}>Can receive push</Typography>
                <Typography variant="h6" sx={{ color: brand.gold, fontWeight: 700 }}>{reach.reachable}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: ink[500] }}>Opted out of marketing</Typography>
                <Typography variant="h6" sx={{ color: ink[900], fontWeight: 700 }}>{reach.optedOut}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: ink[500] }}>Retired device tokens</Typography>
                <Typography variant="h6" sx={{ color: ink[500], fontWeight: 700 }}>{reach.deadTokens}</Typography>
              </Grid>
            </Grid>
            {reach.profiles > 0 && reach.reachable / reach.profiles < 0.5 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Only {Math.round((reach.reachable / reach.profiles) * 100)}% of users can be
                reached. Most have never been asked for notification permission — the app
                only prompts inside chat, bookings and reviews.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* ── Composer ─────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: ink[900] }}>Notification</Typography>
              <Typography variant="body2" sx={{ color: ink[500], mb: 2.5 }}>
                What lands on your customer's lock screen
              </Typography>

              <Stack spacing={2.5}>
                <TextField
                  label="Title" value={title} fullWidth size="small"
                  onChange={(e) => setTitle(e.target.value)}
                  error={overTitle}
                  helperText={`${title.length}/${TITLE_MAX}${overTitle ? ' — iOS will cut this off' : ''}`}
                />
                <TextField
                  label="Message" value={body} fullWidth size="small" multiline minRows={3}
                  onChange={(e) => setBody(e.target.value)}
                  error={overBody}
                  helperText={`${body.length}/${BODY_MAX}${overBody ? ' — longer than two lines will be truncated' : ''}`}
                />

                {/* Optional image. Real, not decorative: the app's
                    NotificationServiceExtension reads image_url from the push
                    payload and attaches it, so this actually shows up. */}
                <Box>
                  <Typography variant="body2" sx={{ color: ink[700], mb: 1 }}>
                    Image <span style={{ color: ink[500] }}>(optional)</span>
                  </Typography>

                  {imageUrl ? (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Box
                        component="img" src={imageUrl} alt="Notification attachment"
                        sx={{
                          maxHeight: 160, maxWidth: '100%',
                          borderRadius: `${radius.md}px`,
                          border: `1px solid ${surface.line}`, display: 'block',
                        }}
                      />
                      <IconButton
                        size="small" onClick={() => setImageUrl('')}
                        sx={{
                          position: 'absolute', top: 6, right: 6,
                          backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      component="label" variant="outlined" size="small"
                      startIcon={uploading ? <CircularProgress size={14} /> : <ImageOutlinedIcon />}
                      disabled={uploading}
                      sx={{ borderStyle: 'dashed', color: ink[700], borderColor: surface.line }}
                    >
                      {uploading ? 'Uploading…' : 'Add an image'}
                      <input
                        type="file" hidden accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => uploadImage(e.target.files?.[0])}
                      />
                    </Button>
                  )}
                  <Typography variant="caption" sx={{ color: ink[500], display: 'block', mt: 0.75 }}>
                    JPG, PNG, GIF or WebP · under 5MB · shown when the notification is expanded
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* ── Recipients ─────────────────────────────────────── */}
          <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: ink[900] }}>Recipients</Typography>
              <Typography variant="body2" sx={{ color: ink[500], mb: 2 }}>
                Counts are live, and are exactly who gets messaged
              </Typography>

              {countsLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              <RadioGroup value={audience} onChange={(e) => setAudience(e.target.value)}>
                {AUDIENCE_OPTIONS.map((opt) => {
                  const n = opt.value === 'all' ? counts.all
                    : opt.value === 'inactive_days' ? counts.inactive
                    : (userType === 'Stylist' ? counts.stylist : counts.client);
                  const active = audience === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => setAudience(opt.value)}
                      sx={{
                        border: `1px solid ${active ? brand.gold : surface.line}`,
                        backgroundColor: active ? surface.sunken : 'transparent',
                        borderRadius: `${radius.md}px`,
                        px: 1.5, py: 1, mb: 1.5, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}
                    >
                      <FormControlLabel
                        value={opt.value}
                        control={<Radio size="small" sx={{ '&.Mui-checked': { color: brand.gold } }} />}
                        sx={{ flex: 1, mr: 0 }}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: ink[900] }}>
                              {opt.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: ink[500] }}>
                              {opt.detail}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        size="small"
                        label={n == null ? '—' : `${n} ${n === 1 ? 'person' : 'people'}`}
                        sx={{
                          backgroundColor: active ? brand.gold : surface.sunken,
                          color: active ? '#fff' : ink[700],
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  );
                })}
              </RadioGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                {audience === 'inactive_days' && (
                  <TextField
                    label="Not opened in (days)" type="number" size="small" value={inactiveDays}
                    onChange={(e) => setInactiveDays(e.target.value)} sx={{ width: 200 }}
                    helperText="1 = since yesterday · 7 = a week"
                  />
                )}
                {audience === 'user_type' && (
                  <TextField
                    select label="Account type" value={userType} size="small" sx={{ width: 200 }}
                    onChange={(e) => setUserType(e.target.value)}
                  >
                    <MenuItem value="Client">Clients</MenuItem>
                    <MenuItem value="Stylist">Stylists</MenuItem>
                  </TextField>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={useSample} onChange={(e) => setUseSample(e.target.checked)}
                  size="small" sx={{ '& .Mui-checked': { color: brand.gold } }}
                />
                <ShuffleIcon fontSize="small" sx={{ color: ink[500] }} />
                <Typography variant="body2" sx={{ color: ink[700] }}>
                  Send to a random sample first
                </Typography>
                {useSample && (
                  <TextField
                    type="number" size="small" value={sampleSize}
                    onChange={(e) => setSampleSize(e.target.value)}
                    sx={{ width: 110, ml: 1 }}
                  />
                )}
              </Stack>
              {useSample && (
                <Typography variant="caption" sx={{ color: ink[500], ml: 6 }}>
                  Try the message on a few people before it goes to everyone.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* ── Delivery ───────────────────────────────────────── */}
          <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: ink[900], mb: 1 }}>Delivery</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={scheduleLater} onChange={(e) => setScheduleLater(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2" sx={{ color: ink[700] }}>Schedule for later</Typography>
                {scheduleLater && (
                  <TextField
                    type="datetime-local" size="small" value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ width: 240, ml: 2 }}
                  />
                )}
              </Stack>
              {!scheduleLater && (
                <Typography variant="caption" sx={{ color: ink[500], ml: 6 }}>
                  Sends the moment you confirm.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Summary + preview ────────────────────────────────── */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: ink[900], mb: 2 }}>Summary</Typography>

              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: ink[500] }}>Audience</Typography>
                  <Typography variant="body2" sx={{ color: ink[900], fontWeight: 600 }}>
                    {audienceSummary()}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: ink[500] }}>Will reach</Typography>
                  <Typography variant="h5" sx={{ color: brand.gold, fontWeight: 700 }}>
                    {willReach == null ? '—' : willReach}
                  </Typography>
                  {useSample && selectedCount != null && willReach !== selectedCount && (
                    <Typography variant="caption" sx={{ color: ink[500] }}>
                      a random sample of {selectedCount}
                    </Typography>
                  )}
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: ink[500] }}>Delivery</Typography>
                  <Typography variant="body2" sx={{ color: ink[900] }}>
                    {scheduleLater && scheduledFor
                      ? new Date(scheduledFor).toLocaleString()
                      : 'Immediately on confirm'}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1} sx={{ mt: 3 }}>
                <Button
                  variant="contained" fullWidth startIcon={<SendIcon />}
                  disabled={!canSave || busy || willReach === 0}
                  sx={{ backgroundColor: brand.gold, '&:hover': { backgroundColor: brand.goldDeep } }}
                  onClick={async () => {
                    const saved = await saveCampaign('draft');
                    if (saved) setConfirm(saved);
                  }}
                >
                  Send now
                </Button>
                <Button
                  variant="outlined" fullWidth startIcon={<ScheduleIcon />}
                  disabled={!canSave || busy || !scheduleLater || !scheduledFor}
                  onClick={async () => {
                    if (await saveCampaign('scheduled')) {
                      notify('Scheduled', 'success'); resetComposer();
                    }
                  }}
                >
                  Schedule
                </Button>
                <Button
                  variant="text" fullWidth startIcon={<SaveIcon />}
                  disabled={!canSave || busy}
                  onClick={async () => {
                    if (await saveCampaign('draft')) {
                      notify('Saved as draft', 'success'); resetComposer();
                    }
                  }}
                >
                  Save as draft
                </Button>
              </Stack>

              {willReach === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nobody matches this audience yet, so there is nothing to send.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Lock-screen preview. Shaped like the real thing so the length of
              the message is judged the way the customer will see it. */}
          <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: ink[900], mb: 2 }}>Preview</Typography>
              <Box sx={{
                backgroundColor: ink[900], borderRadius: '22px', p: 2,
                backgroundImage: 'linear-gradient(160deg, #2b2620 0%, #17140F 100%)',
              }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', textAlign: 'center', mb: 1.5 }}>
                  now
                </Typography>
                <Box sx={{
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  borderRadius: `${radius.lg}px`, p: 1.5,
                  display: 'flex', gap: 1.25, alignItems: 'flex-start',
                }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: '8px',
                    backgroundColor: brand.gold, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 15,
                  }}>L</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: ink[500], textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      LIVLIFE
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: ink[900], lineHeight: 1.25 }}>
                      {title || 'Your title appears here'}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: ink[700], lineHeight: 1.3,
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {body || 'And your message here, trimmed the way iOS trims it.'}
                    </Typography>
                    {imageUrl && (
                      <Box
                        component="img" src={imageUrl} alt=""
                        sx={{
                          mt: 1, width: '100%', maxHeight: 110,
                          objectFit: 'cover', borderRadius: `${radius.sm}px`,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── History ─────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card, mt: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" sx={{ color: ink[900] }}>Campaigns</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small" placeholder="Search title or message"
                value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: ink[500] }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select size="small" value={statusFilter} sx={{ width: 150 }}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>
              <ToggleButtonGroup
                size="small" exclusive value={view}
                onChange={(_, v) => v && setView(v)}
              >
                <ToggleButton value="table"><ViewListIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {loading ? <CircularProgress size={24} /> : view === 'grid' ? (
            <Grid container spacing={2}>
              {visibleCampaigns.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: ink[500] }}>
                    {campaigns.length === 0 ? 'Nothing sent yet.' : 'Nothing matches that search.'}
                  </Typography>
                </Grid>
              )}
              {visibleCampaigns.map((c) => (
                <Grid item xs={12} sm={6} md={4} key={c.id}>
                  <Box sx={{
                    border: `1px solid ${surface.line}`,
                    borderRadius: `${radius.md}px`, p: 2, height: '100%',
                    display: 'flex', flexDirection: 'column', gap: 1,
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Chip size="small" label={prettyStatus(c.status)} color={STATUS_COLOR[c.status] || 'default'} />
                      <Stack direction="row">
                        {['draft', 'scheduled', 'failed'].includes(c.status) && (
                          <IconButton size="small" onClick={() => setConfirm(c)} disabled={busy}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => setDeleting(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                    {c.image_url && (
                      <Box component="img" src={c.image_url} alt=""
                        sx={{ width: '100%', height: 96, objectFit: 'cover', borderRadius: `${radius.sm}px` }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ink[900] }}>
                      {c.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: ink[500], flex: 1 }}>
                      {c.body}
                    </Typography>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: ink[500] }}>
                        {c.audience === 'inactive_days'
                          ? `Inactive ${c.inactive_days ?? 7}d`
                          : c.audience === 'user_type' ? `${prettyStatus(c.user_type)}s` : 'Everyone'}
                      </Typography>
                      {c.recipient_count != null ? (
                        <Button size="small" onClick={() => openRecipients(c)}
                          sx={{ minWidth: 0, p: 0, color: brand.gold, fontWeight: 600 }}>
                          {c.recipient_count} delivered
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: ink[500] }}>—</Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Notification</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Delivered</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCampaigns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>
                        {campaigns.length === 0 ? 'Nothing sent yet.' : 'Nothing matches that search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleCampaigns.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.title}</Typography>
                      <Typography variant="caption" sx={{ color: ink[500] }}>{c.body}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {c.audience === 'inactive_days'
                          ? `Inactive ${c.inactive_days ?? 7}d`
                          : c.audience === 'user_type' ? `${prettyStatus(c.user_type)}s` : 'Everyone'}
                        {c.sample_size ? ` · sample ${c.sample_size}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={prettyStatus(c.status)} color={STATUS_COLOR[c.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      {c.recipient_count != null ? (
                        <Button size="small" onClick={() => openRecipients(c)} sx={{ minWidth: 0, color: brand.gold }}>
                          {c.recipient_count}
                        </Button>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {c.sent_at
                          ? new Date(c.sent_at).toLocaleString()
                          : c.scheduled_for
                            ? `Scheduled ${new Date(c.scheduled_for).toLocaleString()}`
                            : new Date(c.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {['draft', 'scheduled', 'failed'].includes(c.status) && (
                        <Tooltip title="Send this now">
                          <IconButton size="small" onClick={() => setConfirm(c)} disabled={busy}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleting(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmation. The number is repeated here deliberately — it is the
          last chance to notice that "everyone" is more people than expected. */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send to {willReach ?? confirm?.recipient_count ?? '—'} people?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{confirm?.title}</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: ink[500] }}>{confirm?.body}</Typography>
          <Alert severity="warning">
            This arrives on people's phones immediately and cannot be recalled.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained" disabled={busy}
            sx={{ backgroundColor: brand.gold, '&:hover': { backgroundColor: brand.goldDeep } }}
            onClick={() => send(confirm)}
          >
            {busy ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Who actually received it. The table already records this per person;
          without somewhere to read it, "delivered: 6" is a number with no
          way to check. */}
      <Dialog open={!!recipients} onClose={() => setRecipients(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delivery — {recipients?.campaign?.title}
        </DialogTitle>
        <DialogContent>
          {(recipients?.rows || []).length === 0 ? (
            <Typography variant="body2" sx={{ color: ink[500] }}>
              No delivery records for this campaign.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(recipients?.rows || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.phone_number}</TableCell>
                    <TableCell>
                      <Chip size="small"
                        label={r.delivered ? 'Delivered' : 'Not reached'}
                        color={r.delivered ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(r.sent_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipients(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete this campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            “{deleting?.title}” will be removed, along with its record of who received it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" onClick={() => remove(deleting)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
